import React, { useState, useEffect } from 'react';
import { Card, Button, Select, Space, Tag, Modal, Divider, Spin, Empty, message } from 'antd';
import Table from 'components/Common/Table';
import { DownloadOutlined, EyeOutlined, FileTextOutlined } from '@ant-design/icons';
import * as adminactions from 'store/actions/AuthActions';
import VerifyIdentityCard from 'components/Auth/VerifyIdentityCard';
import dayjs from 'dayjs';
import axios from 'axios';
import useUrlFilters from 'hooks/useUrlFilters';

const { Option } = Select;

// Year lives in the URL (ps_ prefix: this is a tab of My Profile). No default:
// the fallback is the current year, and a link must keep its year.
const URL_FILTERS = {
  year: { type: 'string' }, // a year or 'all'
};

const Payslips = ({ isProfileLocked = false }) => {
  const [revealed, setRevealed] = useState(false);
  const [urlFilters, setUrlFilters] = useUrlFilters(URL_FILTERS, { prefix: 'ps_' });
  const selectedYear = urlFilters.year === 'all' ? 'all' : (Number(urlFilters.year) || dayjs().year());
  const setSelectedYear = (value) => setUrlFilters({ year: value });
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [payslipData, setPayslipData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [years, setYears] = useState([]);

  // Load payslips only after revealed
  useEffect(() => {
    if (revealed) loadPayslips();
  }, [selectedYear, revealed]);

  const loadPayslips = async () => {
    setLoading(true);
    try {
      const data = await adminactions.getMyPayslips(selectedYear !== 'all' ? selectedYear : null);
      if (Array.isArray(data)) {
        setPayslipData(data);
        // Extract unique years from data
        const uniqueYears = [...new Set(data.map(p => p.month_year.split('-')[0]))].sort((a, b) => b - a);
        if (uniqueYears.length > 0) {
          setYears(uniqueYears.map(y => parseInt(y)));
        } else {
          // Default to current year and previous years
          const currentYear = dayjs().year();
          setYears([currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4]);
        }
      }
    } catch (error) {
      console.error("Error loading payslips:", error);
      message.error("Failed to load payslips");
    } finally {
      setLoading(false);
    }
  };

  const handleViewPayslip = (record) => {
    setSelectedPayslip(record);
    setViewModalVisible(true);
  };

  const handleDownloadPayslip = async (record) => {
    if (isProfileLocked) {
      return;
    }
    try {
      message.loading({ content: 'Generating payslip...', key: 'download' });
      const response = await axios.get(`/payroll/payroll-histories/${record.id}/download_payslip/`, {
        responseType: 'blob'
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const monthYear = record.month_year.replace('-', '_');
      link.setAttribute('download', `payslip_${record.employee_code}_${monthYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success({ content: 'Payslip downloaded successfully!', key: 'download' });
    } catch (error) {
      console.error("Error downloading payslip:", error);
      message.error({ content: 'Failed to download payslip', key: 'download' });
    }
  };

  const columns = [
    {
      title: 'Month/Year',
      dataIndex: 'month_year',
      key: 'month_year',
      width: 140,
      render: (text) => {
        if (!text) return "-";
        return dayjs(text, "YYYY-MM").format("MMMM YYYY");
      },
      sorter: (a, b) => dayjs(a.month_year, "YYYY-MM").unix() - dayjs(b.month_year, "YYYY-MM").unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const colorMap = {
          GENERATED: 'blue',
          APPROVED: 'green',
          PAID: 'purple',
          CANCELLED: 'red',
        };
        return (
          <Tag color={colorMap[status] || 'default'}>
            {status || 'GENERATED'}
          </Tag>
        );
      },
    },
    {
      title: 'Gross Salary',
      dataIndex: 'gross_salary',
      key: 'gross_salary',
      width: 130,
      align: 'right',
      render: (amount) => amount ? `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-',
    },
    {
      title: 'Deductions',
      dataIndex: 'total_deductions',
      key: 'total_deductions',
      width: 120,
      align: 'right',
      render: (amount) => amount ? `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-',
    },
    {
      title: 'Net Salary',
      dataIndex: 'net_salary_rounded_off',
      key: 'net_salary_rounded_off',
      width: 130,
      align: 'right',
      render: (amount) => <strong>₹{parseFloat(amount || 0).toLocaleString('en-IN')}</strong>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleViewPayslip(record)}
          >
            View
          </Button>
          <Button
            icon={<DownloadOutlined />}
            size="small"
            onClick={() => handleDownloadPayslip(record)}
            disabled={isProfileLocked}
            title={isProfileLocked ? "Profile locked - Cannot download" : "Download Payslip"}
          >
            Download
          </Button>
        </Space>
      ),
    },
  ];

  const renderEarningsBreakdown = (breakdown) => {
    if (!breakdown || Object.keys(breakdown).length === 0) return "-";
    return (
      <div style={{ width: '100%' }}>
        {Object.entries(breakdown).map(([key, value]) => (
          <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span>{key}:</span>
            <span style={{ fontFamily: 'monospace' }}>₹{parseFloat(value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderDeductionsBreakdown = (breakdown) => {
    if (!breakdown || Object.keys(breakdown).length === 0) return "-";
    return (
      <div style={{ width: '100%' }}>
        {Object.entries(breakdown).map(([key, value]) => (
          <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span>{key}:</span>
            <span style={{ color: '#ff4d4f', fontFamily: 'monospace' }}>₹{parseFloat(value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
        ))}
      </div>
    );
  };

  if (!revealed) {
    return (
      <VerifyIdentityCard
        title="My Payslips"
        description="Enter your password to view payslip details"
        submitLabel="Verify & View Payslips"
        onVerified={() => setRevealed(true)}
      />
    );
  }

  return (
    <div style={{ height: '100vh', overflow: 'hidden' }}>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileTextOutlined />
            My Payslips
          </div>
        }
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <Space style={{ marginBottom: '16px' }} wrap>
          <Select
            value={selectedYear}
            style={{ width: 120 }}
            onChange={setSelectedYear}
            placeholder="Select Year"
          >
            <Option value="all">All Years</Option>
            {years.map(year => (
              <Option key={year} value={year}>{year}</Option>
            ))}
          </Select>
        </Space>

        <div style={{ flex: 1, overflow: 'hidden' }}>
          <Spin spinning={loading}>
            {payslipData.length === 0 && !loading ? (
              <Empty description="No payslips found" />
            ) : (
              <Table
                columns={columns}
                dataSource={payslipData}
                rowKey="id"
                pagination={{
                  defaultPageSize: 12,
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} payslips`,
                }}
                scroll={{ x: 800, y: 400 }}
                size="middle"
              />
            )}
          </Spin>
        </div>
      </Card>

      <Modal
        title={
          selectedPayslip ?
            `Payslip - ${dayjs(selectedPayslip.month_year, "YYYY-MM").format("MMMM YYYY")}` :
            'Payslip Details'
        }
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button
            key="download"
            icon={<DownloadOutlined />}
            onClick={() => handleDownloadPayslip(selectedPayslip)}
            disabled={isProfileLocked}
            title={isProfileLocked ? "Profile locked - Cannot download" : "Download PDF"}
          >
            Download PDF
          </Button>,
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedPayslip && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <p><strong>Employee Name:</strong> {selectedPayslip.employee_name}</p>
                <p><strong>Employee Code:</strong> {selectedPayslip.employee_code}</p>
                <p><strong>Department:</strong> {selectedPayslip.department_name || '-'}</p>
                <p><strong>Designation:</strong> {selectedPayslip.designation || '-'}</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p><strong>Pay Period:</strong> {dayjs(selectedPayslip.month_year, "YYYY-MM").format("MMMM YYYY")}</p>
                <p><strong>Working Days:</strong> {selectedPayslip.total_working_days}</p>
                <p><strong>Effective Days:</strong> {selectedPayslip.effective_days}</p>
                <p><strong>Status:</strong> <Tag color={selectedPayslip.status === 'PAID' ? 'purple' : selectedPayslip.status === 'APPROVED' ? 'green' : 'blue'}>{selectedPayslip.status}</Tag></p>
              </div>
            </div>

            <Divider />

            <Card size="small" title="Attendance Summary" style={{ marginBottom: 16 }} headStyle={{ backgroundColor: '#f0f5ff' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
                <div>
                  <div style={{ color: '#8c8c8c', fontSize: 12 }}>Working Days</div>
                  <div style={{ fontWeight: 600 }}>{selectedPayslip.total_working_days || 0}</div>
                </div>
                <div>
                  <div style={{ color: '#8c8c8c', fontSize: 12 }}>Present</div>
                  <div style={{ fontWeight: 600 }}>{parseFloat(selectedPayslip.days_present || 0)}</div>
                </div>
                <div>
                  <div style={{ color: '#8c8c8c', fontSize: 12 }}>Paid Leave</div>
                  <div style={{ fontWeight: 600 }}>{parseFloat(selectedPayslip.paid_leaves || 0)}</div>
                </div>
                <div>
                  <div style={{ color: '#8c8c8c', fontSize: 12 }}>Comp-Off</div>
                  <div style={{ fontWeight: 600, color: '#1677ff' }}>{parseFloat(selectedPayslip.comp_off_days || 0)}</div>
                </div>
                <div>
                  <div style={{ color: '#8c8c8c', fontSize: 12 }}>LOP</div>
                  <div style={{ fontWeight: 600, color: '#cf1322' }}>{parseFloat(selectedPayslip.lop_days || 0)}</div>
                </div>
              </div>
            </Card>

            <div style={{ display: 'flex', gap: 16 }}>
              <Card size="small" title="Earnings" style={{ flex: 1 }} headStyle={{ backgroundColor: '#f6ffed' }}>
                {renderEarningsBreakdown(selectedPayslip.earnings_breakdown)}
                <Divider style={{ margin: '8px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                  <span>Total Earnings:</span>
                  <span>₹{parseFloat(selectedPayslip.total_earnings || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
              </Card>

              <Card size="small" title="Deductions" style={{ flex: 1 }} headStyle={{ backgroundColor: '#fff2e8' }}>
                {renderDeductionsBreakdown(selectedPayslip.deductions_breakdown)}
                <Divider style={{ margin: '8px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                  <span>Total Deductions:</span>
                  <span style={{ color: '#ff4d4f' }}>₹{parseFloat(selectedPayslip.total_deductions || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
              </Card>
            </div>

            <Divider />

            <Card size="small" style={{ backgroundColor: '#f0f9ff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '18px', fontWeight: 'bold' }}>
                <span>Net Salary:</span>
                <span style={{ color: '#1890ff' }}>₹{parseFloat(selectedPayslip.net_salary_rounded_off || 0).toLocaleString('en-IN')}</span>
              </div>
            </Card>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Payslips;
